import Debug from 'debug';
import { FieldArray, useFormikContext } from 'formik';
import * as React from 'react';
import { Button, FormGroup, InputGroup, Table } from 'react-bootstrap';
import {
  cloneField,
  DataRowAction,
  Entity,
  Field,
  getFieldArray,
  getFieldColumnAlign,
  isAllowAddRows,
  isAllowDeleteRows,
  isFieldEditable,
  Row,
} from 'gossamer-unified';
import { ErrorBoundary, General, notify, Section } from '../index';
import { FormField } from './FormField';
import { FormRow, getInitialValuesNewRow } from './Utils';

const debug = Debug('gfe/se/FormGrid');

export interface FormGridProps<T extends Row> {
  addRowFieldId?: string;
  alias?: string;
  columns?: [Field, string][];
  entity: Entity<T>;
  onAddRow?: (newData: FormRow<T>, newValue?: string) => void;
  renderFooter?: () => React.JSX.Element;
  renderHeader?: (props: FormGridHeaderProps<T>) => React.JSX.Element;
  renderRow?: (props: FormGridRowProps<T>) => React.JSX.Element;
  uniqueOnAddRowField?: boolean;
}

export const FormGrid = <T extends {}>(props: FormGridProps<T>): React.JSX.Element => {
  // debug("start of render FormGrid");
  const { setFieldValue, values } = useFormikContext<Record<string, unknown>>();
  const identifier = props.alias || props.entity.id;
  const setRowDelete = (index: number) => setFieldValue(`${identifier}[${index}]._deleting`, true);
  const formValues = values as Record<string, unknown>;
  if (!formValues[identifier] || !Array.isArray(formValues[identifier])) {
    throw new Error(`grid incorrectly initialized: values[${identifier}] does not exist`);
  }
  const addRowFieldIdList = React.useMemo(() => {
    const addRowFieldId = props.addRowFieldId;
    if (!addRowFieldId) {
      return [];
    }
    return (formValues[identifier] as FormRow<T>[])
      .filter((row) => row._action !== 'D' && !row._deleting)
      .map((row) => String((row as Record<string, unknown>)[addRowFieldId]));
  }, [formValues[identifier], props.addRowFieldId]);
  return (
    <ErrorBoundary>
      <FieldArray name={identifier}>
        {({ push }) => {
          const addRow = (newValue?: string) => {
            if (props.addRowFieldId && !newValue) {
              const error = `new value not supplied`;
              notify([error, 'danger']);
              throw new Error(error);
            }
            if (props.uniqueOnAddRowField && newValue && addRowFieldIdList.indexOf(newValue) > -1) {
              const error = `value ${newValue} already in list`;
              notify([error, 'danger']);
              throw new Error(error);
            }
            const data = getInitialValuesNewRow(props.entity);
            if (props.addRowFieldId) {
              (data as Record<string, unknown>)[props.addRowFieldId] = newValue;
            }
            if (props.onAddRow) {
              props.onAddRow(data, newValue);
            }
            push(data);
          };
          return (
            <FormGridMain
              {...props}
              addRow={addRow}
              excludeValues={!!props.uniqueOnAddRowField ? addRowFieldIdList : []}
              identifier={identifier}
              rowValues={formValues[identifier] as FormRow<T>[]}
              setRowDelete={setRowDelete}
            />
          );
        }}
      </FieldArray>
    </ErrorBoundary>
  );
};

export interface FormGridMainProps<T extends Row> extends FormGridProps<T> {
  addRow: (newValue?: string) => void;
  excludeValues: string[];
  identifier: string;
  rowValues: FormRow<T>[];
  setRowDelete: (index: number) => void;
}

export const FormGridMain = <T extends {}>(props: FormGridMainProps<T>): React.JSX.Element => {
  const columns =
    props.columns || getFieldArray(props.entity).filter(([field, _]: [Field, string]) => field.listColumn);
  const LocalGridFooter = props.renderFooter || (() => null);
  const LocalGridHeader = props.renderHeader || FormGridHeader;
  const LocalGridRow = props.renderRow || FormGridRow;
  let addRowField = null;
  const allowRowAdd = isAllowAddRows(props.entity);
  const allowRowDelete = isAllowDeleteRows(props.entity);
  if (allowRowAdd && !!props.addRowFieldId) {
    addRowField = cloneField((props.entity.fields as Record<string, Field>)[props.addRowFieldId]);
    addRowField.mandatory = false;
    addRowField.editable = true;
    addRowField.label = 'Add a new ' + addRowField.label;
    (props.entity.fields as Record<string, Field>)[props.addRowFieldId].editable = false;
  }

  const deleteRow = (index: number) => {
    if (index < 0 || index >= props.rowValues.length) {
      throw new Error(`index ${index} is out of range 0 - ${props.rowValues.length - 1}`);
    }
    debug('calling grid.deleteRow()', props.identifier, index);
    props.setRowDelete(index);
  };
  const children = props.rowValues.map((row: FormRow<T>, index: number) => {
    return (
      <LocalGridRow
        key={row._original_key || row._temp_new_key}
        action={row._action}
        allowRowDelete={allowRowDelete}
        columns={columns}
        deleteRow={deleteRow}
        identifier={props.identifier}
        isDeleting={!!row._deleting}
        index={index}
      />
    );
  });
  return (
    <div id={props.identifier}>
      <Table responsive striped bordered>
        <LocalGridHeader allowRowDelete={allowRowDelete} columns={columns} />
        <tbody>{children}</tbody>
        <LocalGridFooter />
      </Table>
      {allowRowAdd && (
        <div className="mb20px">
          {addRowField ? (
            <FormGridRowAddWithControl
              addRow={props.addRow}
              addRowField={addRowField}
              excludeValues={props.excludeValues}
            />
          ) : (
            <FormGridRowAddWithButton addRow={props.addRow} />
          )}
        </div>
      )}
    </div>
  );
};

export interface FormGridHeaderProps<T extends Row> {
  allowRowDelete: boolean;
  columns: [Field, string][];
}

export const FormGridHeader = <T extends {}>(props: FormGridHeaderProps<T>): React.JSX.Element => {
  return (
    <thead>
      <tr>
        {props.allowRowDelete && <th>&nbsp;</th>}
        {props.columns.map(([field, fieldId]: [Field, string]) => (
          <th key={fieldId} className={`cellAlign-${getFieldColumnAlign(field)}`}>
            {field.label}
            {field.mandatory ? ' *' : ''}
            {field.description && <a title={field.description}>&nbsp;ⓘ</a>}
          </th>
        ))}
      </tr>
    </thead>
  );
};

export interface FormGridRowProps<T extends Row> {
  action: DataRowAction;
  allowRowDelete: boolean;
  columns: [Field, string][];
  deleteRow: (index: number) => void;
  identifier: string;
  isDeleting: boolean;
  index: number;
}

export const FormGridRow = <T extends {}>(props: FormGridRowProps<T>): React.JSX.Element => {
  if (props.action === 'D' || props.isDeleting) {
    return <></>;
  }
  return (
    <tr>
      {props.allowRowDelete && (
        <td key="~deletion">
          <button type="button" onClick={() => props.deleteRow(props.index)} className="ControlIcon">
            ✖
          </button>
        </td>
      )}
      {props.columns.map(([field, fieldId]: [Field, string]) => {
        return (
          <td key={fieldId} className={`cellAlign-${getFieldColumnAlign(field)}`}>
            <FormGroup controlId={fieldId}>
              <FormField
                editable={isFieldEditable(field, props.action)}
                field={field}
                fullId={`${props.identifier}[${props.index}].${fieldId}`}
              />
            </FormGroup>
          </td>
        );
      })}
    </tr>
  );
};

export interface FormGridRowAddWithButtonProps {
  addRow: () => void;
}

export const FormGridRowAddWithButton = (props: FormGridRowAddWithButtonProps) => (
  <div>
    <Button variant="primary" size="sm" onClick={() => props.addRow()} className="main">
      Add New Row
    </Button>
  </div>
);

export interface FormGridRowAddWithControlProps {
  addRow: (newValue: string) => void;
  addRowField: Field;
  excludeValues: string[];
}

export const FormGridRowAddWithControl = (props: FormGridRowAddWithControlProps) => (
  <InputGroup size="sm">
    <InputGroup.Text>{props.addRowField.label}</InputGroup.Text>
    <General
      className="addNewRow"
      clearOnSelection={true}
      editable={true}
      excludeValues={props.excludeValues}
      fieldId="addNewRow"
      field={props.addRowField}
      handleChange={(newValue) => {
        if (newValue) {
          props.addRow(newValue);
        }
      }}
      value=""
    />
  </InputGroup>
);

interface GridProps<T extends Row> {
  addRowFieldId?: string;
  columns?: [Field, string][];
  entity: Entity<T>;
  onAddRow?: (newData: FormRow<T>, newValue?: string) => void;
  title?: string;
  subtitle?: string;
  valuesKey: string;
}

export const Grid = <T extends Row>(props: GridProps<T>): React.JSX.Element => {
  return (
    <Section title={props.title} subtitle={props.subtitle}>
      <FormGrid
        addRowFieldId={props.addRowFieldId}
        alias={props.valuesKey}
        columns={props.columns}
        entity={props.entity}
        onAddRow={props.onAddRow}
        uniqueOnAddRowField={!!props.addRowFieldId}
      />
    </Section>
  );
};
